import {
  UpdateUserError,
  UpdateUserErrorResponseReason,
} from 'tools/bff/generated/user/user_administration_pb';
import type { EditUserDetailsFormValues } from 'tools/users/EditUserDetailsModal';

// Operator-facing copy keyed by the typed reason GDUS returns. Because this is a
// Record<Enum, string> (not Partial), TypeScript fails the build if a new proto reason is added
// without copy — the copy lives here, not in the server. Strings are verbatim the ones GDUS used
// to hard-code, so this is a relocation, not a copy change.
const REASON_COPY: Record<UpdateUserErrorResponseReason, string> = {
  [UpdateUserErrorResponseReason.UNSPECIFIED]: "We couldn't update the account. Please try again.",
  [UpdateUserErrorResponseReason.EMAIL_ALREADY_IN_USE]:
    'That email address is already in use by another account.',
  [UpdateUserErrorResponseReason.PHONE_ALREADY_IN_USE]:
    'That phone number is already in use by another account.',
  [UpdateUserErrorResponseReason.SERVICE_UNAVAILABLE]:
    "We couldn't update the account right now. Please try again in a moment.",
};

// Reasons that map to a specific form field. Reasons absent here (SERVICE_UNAVAILABLE, UNSPECIFIED)
// are surfaced in the modal's general error area instead.
const REASON_FIELD: Partial<
  Record<UpdateUserErrorResponseReason, keyof EditUserDetailsFormValues>
> = {
  [UpdateUserErrorResponseReason.EMAIL_ALREADY_IN_USE]: 'emailAddress',
  [UpdateUserErrorResponseReason.PHONE_ALREADY_IN_USE]: 'phoneNumber',
};

export interface ResolvedUpdateUserError {
  // The form field to attach the message to, or undefined for the general error area.
  field?: keyof EditUserDetailsFormValues;
  message: string;
}

/**
 * Resolves a typed {@link UpdateUserError} into a message and (optionally) the form field it
 * belongs to. Known reasons render their copy; an UNSPECIFIED reason renders the server-provided
 * pass-through `message` (Resy/Tock validation text) and falls back to generic copy when empty.
 */
export const resolveUpdateUserError = (error: UpdateUserError): ResolvedUpdateUserError => {
  const message =
    error.reason === UpdateUserErrorResponseReason.UNSPECIFIED && error.message
      ? error.message
      : REASON_COPY[error.reason];
  return { field: REASON_FIELD[error.reason], message };
};
